<template>
  <div>
    <!-- Mandatory modal: No timezone set -->
    <b-modal
      ref="timezoneModal"
      :title="$t('global.timezonePrompt.title')"
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      hide-footer
      size="md"
    >
      <div class="tw-py-3">
        <div class="tw-mb-4 tw-text-gray-700">
          <p class="tw-mb-2">
            <i class="icon-info tw-mr-2 tw-text-blue-500"></i>
            {{ $t("global.timezonePrompt.description") }}
          </p>
        </div>

        <timezone-selector
          v-model="selectedTimezone"
          :label="$t('components.timezoneSelector.label')"
        />

        <div class="tw-flex tw-justify-end tw-mt-4">
          <b-button
            variant="primary"
            :disabled="!selectedTimezone || isSaving"
            @click="saveTimezone"
          >
            <span v-if="isSaving">
              <i class="icon-loader tw-animate-spin tw-mr-2"></i>
              {{ $t("global.saving") }}
            </span>
            <span v-else>
              <i class="icon-check tw-mr-2"></i>
              {{ $t("global.save") }}
            </span>
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Optional modal: Timezone mismatch warning -->
    <b-modal
      ref="timezoneMismatchModal"
      :title="$t('global.timezoneMismatch.title')"
      hide-footer
      size="md"
    >
      <div class="tw-pb-3">
        <div class="tw-mb-4">
          <div class="tw-flex tw-items-start tw-p-3 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded tw-mb-3">
            <i class="icon-alert-triangle tw-mr-2 tw-mt-0.5 tw-text-amber-600"></i>
            <div class="tw-text-sm tw-text-amber-900">
              {{ $t("global.timezoneMismatch.description") }}
            </div>
          </div>

          <div class="tw-space-y-2 tw-text-sm">
            <div class="tw-flex tw-justify-between tw-py-2 tw-px-3 tw-bg-gray-50 tw-rounded">
              <span class="tw-font-medium tw-text-gray-700">{{ $t("global.timezoneMismatch.saved") }}:</span>
              <span class="tw-text-gray-900">{{ savedTimezoneDisplay }}</span>
            </div>
            <div class="tw-flex tw-justify-between tw-py-2 tw-px-3 tw-bg-blue-50 tw-rounded">
              <span class="tw-font-medium tw-text-blue-700">{{ $t("global.timezoneMismatch.detected") }}:</span>
              <span class="tw-text-blue-900 tw-font-medium">{{ detectedTimezoneDisplay }}</span>
            </div>
          </div>
        </div>

        <div class="tw-flex tw-justify-between tw-mt-4">
          <b-button
            variant="light"
            @click="dismissMismatch"
          >
            {{ $t("global.timezoneMismatch.keepCurrent") }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="isSaving"
            @click="switchToDetectedTimezone"
          >
            <span v-if="isSaving">
              <i class="icon-loader tw-animate-spin tw-mr-1"></i>
              {{ $t("global.saving") }}
            </span>
            <span v-else>
              <i class="icon-refresh-cw tw-mr-1"></i>
              {{ $t("global.timezoneMismatch.switchToDetected") }}
            </span>
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TimezoneSelector from "@components/TimezoneSelector.vue";
import { mapState } from "vuex";
import { getBrowserTimezone } from "@utils/timezone";

export default {
  name: "TimezonePrompt",
  components: {
    TimezoneSelector,
  },
  data() {
    return {
      selectedTimezone: "",
      isSaving: false,
      detectedTimezone: null,
      hasDismissedMismatch: false,
      dismissalKey: 'timezone-action-broadcast',
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.auth.currentUser,
    }),
    loggedIn() {
      return this.$store.getters["auth/loggedIn"];
    },
    savedTimezoneDisplay() {
      if (!this.currentUser?.timezone) return '';
      return this.formatTimezoneDisplay(this.currentUser.timezone);
    },
    detectedTimezoneDisplay() {
      if (!this.detectedTimezone) return '';
      return this.formatTimezoneDisplay(this.detectedTimezone);
    }
  },
  watch: {
    currentUser: {
      handler(newUser) {
        if (newUser && this.loggedIn) {
          this.checkTimezone();
        }
      },
      immediate: true,
      deep: true,
    },
    loggedIn(val) {
      if (val && this.currentUser) {
        this.checkTimezone();
      }
    },
  },
  mounted() {
    this.detectBrowserTimezone();
    
    // Listen for storage changes from other tabs
    window.addEventListener('storage', this.handleStorageChange);
    
    // Also check on mount in case everything is already loaded
    if (this.loggedIn && this.currentUser) {
      this.checkTimezone();
    }
  },
  
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange);
  },
  methods: {
    handleStorageChange(event) {
      // Listen for timezone action broadcasts from other tabs
      if (event.key === this.dismissalKey && event.newValue) {
        // Another tab dismissed or updated timezone, refetch user state
        this.$store.dispatch('auth/refetchUser').then(() => {
          // Close any open modals after refetch
          this.hasDismissedMismatch = true;
          if (this.$refs["timezoneMismatchModal"]) {
            this.$refs["timezoneMismatchModal"].hide();
          }
          if (this.$refs["timezoneModal"]) {
            this.$refs["timezoneModal"].hide();
          }
        });
      }
    },
    
    broadcastTimezoneAction() {
      // Broadcast to other tabs by writing to localStorage
      // The value doesn't matter, just the change event
      localStorage.setItem(this.dismissalKey, Date.now().toString());
      // Clean up immediately
      localStorage.removeItem(this.dismissalKey);
    },
    
    detectBrowserTimezone() {
      this.detectedTimezone = getBrowserTimezone();
    },
    
    formatTimezoneDisplay(timezone) {
      if (!timezone) return '';
      
      try {
        const displayName = timezone.replace(/_/g, ' ');
        
        // Get current time in that timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        const localTime = formatter.format(new Date());
        
        // Get offset
        const offsetFormatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          timeZoneName: 'shortOffset'
        });
        const parts = offsetFormatter.formatToParts(new Date());
        const offsetPart = parts.find(part => part.type === 'timeZoneName');
        const offset = offsetPart ? offsetPart.value.replace('GMT', 'UTC') : '';
        
        return `${displayName} (${offset}) - ${localTime}`;
      } catch (error) {
        return timezone;
      }
    },
    
    checkTimezone() {
      // Priority 1: No timezone set - show mandatory modal
      if (this.loggedIn && this.currentUser && !this.currentUser.timezone) {
        this.$nextTick(() => {
          this.$refs["timezoneModal"].show();
        });
        return;
      }
      
      // Priority 2: Timezone mismatch - show optional warning
      if (this.currentUser?.timezone) {
        this.selectedTimezone = this.currentUser.timezone;
        
        // Check if dismissal is in session (per-user, per-session)
        const userDismissalKey = `timezone-dismissed-${this.currentUser.id}-${this.currentUser.timezone}`;
        const isDismissed = sessionStorage.getItem(userDismissalKey);
        
        // Check for mismatch with browser timezone
        if (
          this.detectedTimezone && 
          this.detectedTimezone !== this.currentUser.timezone &&
          !this.hasDismissedMismatch &&
          !isDismissed
        ) {
          // Delay the mismatch warning slightly so it doesn't conflict with mandatory modal
          setTimeout(() => {
            this.$nextTick(() => {
              this.$refs["timezoneMismatchModal"]?.show();
            });
          }, 500);
        }
      }
    },
    
    dismissMismatch() {
      this.hasDismissedMismatch = true;
      
      // Store dismissal in sessionStorage (per-user, per-timezone, per-session)
      const userDismissalKey = `timezone-dismissed-${this.currentUser.id}-${this.currentUser.timezone}`;
      sessionStorage.setItem(userDismissalKey, 'true');
      
      this.$refs["timezoneMismatchModal"].hide();
      
      // Broadcast to other tabs
      this.broadcastTimezoneAction();
    },
    
    async switchToDetectedTimezone() {
      if (!this.detectedTimezone) return;
      
      this.isSaving = true;
      
      try {
        await this.$store.dispatch("auth/updateUser", { 
          timezone: this.detectedTimezone 
        });

        this.$refs["timezoneMismatchModal"].hide();
        this.hasDismissedMismatch = true;
        
        // Broadcast to other tabs to refetch user
        this.broadcastTimezoneAction();

        this.emitSuccessNotification();
      } catch (error) {
        this.emitErrorNotification();
      } finally {
        this.isSaving = false;
      }
    },

    async saveTimezone() {
      this.isSaving = true;

      try {
        await this.$store.dispatch("auth/updateUser", {
          timezone: this.selectedTimezone,
        });

        this.$refs["timezoneModal"].hide();
        
        // Broadcast to other tabs to refetch user
        this.broadcastTimezoneAction();

        this.emitSuccessNotification();
      } catch (error) {
        this.emitErrorNotification();
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
